import { useCallback, useEffect, useRef, useState } from 'react';
import {
  cargarCursos,
  cargarProgreso,
  marcarLeccion as marcarLeccionApi,
  type CursoAcademy,
  type ProgresoFila,
} from './academyApi';
import { derivarProgreso, type ProgresoCurso } from './progresoDerivado';

/**
 * Carga de Academia Atlas — ADR-2026-08-25-la-academia-vive-en-supabase.
 *
 * **El CONTENIDO se cachea a nivel módulo y el PROGRESO no.** No es lo mismo:
 * los cursos y sus lecciones cambian cuando alguien edita la base (raro, y no
 * mientras el usuario navega), mientras que el progreso cambia con cada lección
 * que se abre. Cacheando el contenido, moverse entre el listado, un curso y otro
 * curso no vuelve a pedir las mismas tres consultas; releyendo el progreso, el
 * porcentaje nunca queda viejo.
 *
 * La caché es una PROMESA y no el resultado: dos pantallas que montan a la vez
 * comparten el mismo viaje en lugar de disparar dos.
 */

let cacheCursos: Promise<CursoAcademy[]> | null = null;

/** Tira la caché de contenido. Para después de editar cursos en la base. */
export function invalidarCursos(): void {
  cacheCursos = null;
}

function cursosCacheados(): Promise<CursoAcademy[]> {
  if (!cacheCursos) {
    cacheCursos = cargarCursos().catch((e) => {
      // Un fallo no puede quedar cacheado: si no se limpia, la pantalla queda
      // rota hasta recargar el navegador aunque la red vuelva.
      cacheCursos = null;
      throw e;
    });
  }
  return cacheCursos;
}

export interface EstadoAcademia {
  cursos: CursoAcademy[];
  progreso: Map<string, ProgresoFila>;
  cargando: boolean;
  error: string | null;
  /** Vuelve a pedir SÓLO el progreso. Se usa después de escribir. */
  recargarProgreso: () => Promise<void>;
}

/** Los cursos y el progreso del usuario, juntos. */
export function useAcademia(): EstadoAcademia {
  const [cursos, setCursos] = useState<CursoAcademy[]>([]);
  const [progreso, setProgreso] = useState<Map<string, ProgresoFila>>(new Map());
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /* Número de la última lectura pedida
     (ADR-2026-10-07-el-progreso-no-retrocede-por-una-lectura-vieja). Abrir lecciones
     seguidas dispara varias "marcar y releer" superpuestas; si una lectura vieja —que
     todavía no veía la última marca— llega DESPUÉS que la nueva, pisaba el progreso
     con menos lecciones. Sólo se aplica la respuesta de la lectura más reciente: se
     pidió después de todas las marcas anteriores, así que las incluye. */
  const ultimaLectura = useRef(0);

  const recargarProgreso = useCallback(async () => {
    const lectura = ++ultimaLectura.current;
    try {
      const p = await cargarProgreso();
      if (lectura === ultimaLectura.current) setProgreso(p);
    } catch (e) {
      // Un fallo al RELEER el progreso no rompe la pantalla: lo que ya está
      // dibujado sigue siendo válido y la próxima navegación lo vuelve a pedir.
      console.error('[academia] no se pudo releer el progreso', e);
    }
  }, []);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const lectura = ++ultimaLectura.current;
        const [c, p] = await Promise.all([cursosCacheados(), cargarProgreso()]);
        if (!vivo) return;
        setCursos(c);
        // La carga inicial también es una lectura: si una recarga posterior ya
        // respondió, esta trae datos más viejos.
        if (lectura === ultimaLectura.current) setProgreso(p);
      } catch (e) {
        if (!vivo) return;
        console.error('[academia] no se pudo cargar', e);
        setError('No pudimos cargar los cursos. Probá de nuevo en un momento.');
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, []);

  return { cursos, progreso, cargando, error, recargarProgreso };
}

/**
 * Lo mismo, acotado a un curso por su slug, más el `marcarVista` que las
 * pantallas de lección necesitan.
 *
 * `curso` es `null` mientras carga Y también cuando el slug no existe; las
 * pantallas distinguen los dos casos mirando `cargando`.
 */
export function useCurso(slug: string | undefined) {
  const { cursos, progreso, cargando, error, recargarProgreso } = useAcademia();

  const curso = slug ? (cursos.find((c) => c.slug === slug) ?? null) : null;
  const fila = curso ? progreso.get(curso.id) : undefined;

  const progresoCurso: ProgresoCurso | null = curso
    ? derivarProgreso(curso, fila)
    : null;

  const marcarVista = useCallback(
    async (leccionId: string) => {
      try {
        await marcarLeccionApi(leccionId);
        await recargarProgreso();
      } catch (e) {
        // Marcar es idempotente y se reintenta al volver a abrir la lección, así
        // que un fallo acá no vale interrumpir la clase con un cartel.
        console.error('[academia] no se pudo marcar la lección', e);
      }
    },
    [recargarProgreso],
  );

  return {
    cursos,
    curso,
    progreso: progresoCurso,
    cargando,
    error,
    marcarVista,
    recargarProgreso,
  };
}

/**
 * Sólo el catálogo, sin progreso — para la card de la home.
 *
 * La home no necesita saber cómo viene el usuario: muestra cuántos cursos y
 * lecciones hay. Pedir además el progreso sería una consulta más en la pantalla
 * más visitada de la app, para un dato que esa card no dibuja.
 *
 * Comparte la caché con `useAcademia`, así que si el usuario entra después a la
 * academia, el contenido ya está.
 */
export function useCursosCatalogo(): { cursos: CursoAcademy[]; cargando: boolean } {
  const [cursos, setCursos] = useState<CursoAcademy[]>([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let vivo = true;
    cursosCacheados()
      .then((c) => vivo && setCursos(c))
      .catch((e) => console.error('[academia] no se pudo cargar el catálogo', e))
      .finally(() => vivo && setCargando(false));
    return () => {
      vivo = false;
    };
  }, []);

  return { cursos, cargando };
}
